import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Eye, RefreshCw, FileSignature, CheckCircle2, RotateCcw, XCircle, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { contractsAPI, employeeAPI } from '../services/api';
import { confirmToast } from '../lib/confirmToast';

const TYPES = ['permanent', 'fixed_term', 'probation', 'internship', 'consultant', 'part_time', 'temporary'];
const STATUSES = ['draft', 'active', 'renewed', 'expired', 'terminated', 'cancelled'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  draft: 'secondary', active: 'default', expiring: 'secondary', expired: 'destructive',
  renewed: 'outline', terminated: 'destructive', cancelled: 'outline', signed: 'default',
}[s] || 'secondary');

const PAGE_SIZE = 25;

export default function ContractsManagement() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewForm, setRenewForm] = useState({ end_date: '', ctc_annual: '' });
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: '', contract_type: 'fixed_term', title: '', start_date: '', end_date: '',
    ctc_annual: '', notice_period_days: 30, auto_renew: false, terms: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.contract_type = typeFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await contractsAPI.list(params);
      setRows(data.data || []); setTotal(data.total || 0);
    } catch (e) { toast.error('Failed to load contracts'); }
    finally { setLoading(false); }
  }, [page, statusFilter, typeFilter, search]);

  const loadSummary = useCallback(async () => {
    try { const { data } = await contractsAPI.summary(); setSummary(data); } catch (e) { /* */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openCreate = async () => {
    setForm({ employee_id: '', contract_type: 'fixed_term', title: '', start_date: '', end_date: '',
      ctc_annual: '', notice_period_days: 30, auto_renew: false, terms: '' });
    setCreateOpen(true);
    if (employees.length === 0) {
      try {
        const { data } = await employeeAPI.list();
        setEmployees((Array.isArray(data) ? data : data.data || []).filter((e) => e.is_active !== false));
      } catch (e) { /* */ }
    }
  };

  const submitCreate = async () => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    if (!form.contract_type) { toast.error('Select a type'); return; }
    try {
      await contractsAPI.create({
        employee_id: form.employee_id,
        contract_type: form.contract_type,
        title: form.title || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        ctc_annual: form.ctc_annual ? Number(form.ctc_annual) : null,
        notice_period_days: form.notice_period_days ? Number(form.notice_period_days) : null,
        auto_renew: !!form.auto_renew,
        terms: form.terms || null,
      });
      toast.success('Contract created');
      setCreateOpen(false); load(); loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const refreshDetail = async (id) => {
    try { const { data } = await contractsAPI.get(id); setDetail(data); }
    catch (e) { toast.error('Failed to load contract'); }
  };

  const doActivate = (row) => confirmToast({
    message: `Activate contract ${row.contract_number}?`,
    description: 'Marks the contract active and ready for signature.',
    confirmLabel: 'Activate',
    onConfirm: async () => {
      try { await contractsAPI.activate(row.id); toast.success('Contract activated'); load(); loadSummary(); if (detail?.id === row.id) refreshDetail(row.id); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Activate failed'); }
    },
  });

  const doSign = async (row, party) => {
    try { const { data } = await contractsAPI.sign(row.id, party); toast.success(`${titleize(party)} signature recorded`); if (detail?.id === row.id) setDetail(data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sign failed'); }
  };

  const doTerminate = (row) => confirmToast({
    message: `Terminate contract ${row.contract_number}?`,
    description: 'This cannot be undone.',
    confirmLabel: 'Terminate',
    destructive: true,
    onConfirm: async () => {
      try { await contractsAPI.terminate(row.id, 'Terminated'); toast.success('Contract terminated'); load(); loadSummary(); if (detail?.id === row.id) refreshDetail(row.id); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Terminate failed'); }
    },
  });

  const doCancel = (row) => confirmToast({
    message: `Cancel contract ${row.contract_number}?`,
    confirmLabel: 'Cancel Contract',
    destructive: true,
    onConfirm: async () => {
      try { await contractsAPI.remove(row.id); toast.success('Contract cancelled'); load(); loadSummary(); setDetail(null); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
    },
  });

  const openRenew = (row) => { setRenewForm({ end_date: '', ctc_annual: row.ctc_annual || '' }); setDetail(row); setRenewOpen(true); };
  const submitRenew = () => confirmToast({
    message: `Renew contract ${detail?.contract_number}?`,
    description: 'Creates a new linked contract and marks this one as renewed.',
    confirmLabel: 'Renew',
    onConfirm: async () => {
      try {
        await contractsAPI.renew(detail.id, {
          end_date: renewForm.end_date || null,
          ctc_annual: renewForm.ctc_annual ? Number(renewForm.ctc_annual) : null,
        });
        toast.success('Contract renewed'); setRenewOpen(false); setDetail(null); load(); loadSummary();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Renew failed'); }
    },
  });

  const runMaintenance = () => confirmToast({
    message: 'Run contract expiry maintenance?',
    description: 'Flags past-due contracts as expired and auto-renews those configured for renewal.',
    confirmLabel: 'Run',
    onConfirm: async () => {
      try {
        const { data } = await contractsAPI.runMaintenance();
        toast.success(`Processed ${data.processed} — ${data.expired} expired, ${data.renewed} renewed`);
        load(); loadSummary();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Maintenance failed'); }
    },
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const statCards = useMemo(() => ([
    { label: 'Active', value: summary?.active ?? '—' },
    { label: 'Expiring Soon', value: summary?.expiring_soon ?? '—', warn: (summary?.expiring_soon || 0) > 0 },
    { label: 'Expired', value: summary?.expired ?? '—', warn: (summary?.expired || 0) > 0 },
    { label: 'Total', value: summary?.total ?? '—' },
  ]), [summary]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><FileSignature className="h-5 w-5" /> Contracts</h2>
          <p className="text-sm text-muted-foreground">Employment contracts — issuance, e-signature, renewals and expiry alerts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runMaintenance}>
            <RefreshCw className="h-4 w-4 mr-1" /> Run Expiry Check
          </Button>
          <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> New Contract</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {statCards.map((s) => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {s.warn ? <AlertTriangle className="h-3 w-3 text-amber-500" /> : null}{s.label}
            </p>
            <p className={`text-2xl font-semibold ${s.warn ? 'text-amber-600' : ''}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter || '__all__'} onValueChange={(v) => { setStatusFilter(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Type</Label>
          <Select value={typeFilter || '__all__'} onValueChange={(v) => { setTypeFilter(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Types</SelectItem>
              {TYPES.map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <Label className="text-xs">Search</Label>
          <Input className="h-9" placeholder="Name, ID or contract no." value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(1), load())} />
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => { setPage(1); load(); loadSummary(); }}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Contract</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>End / Expiry</TableHead>
              <TableHead>E-Sign</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No contracts.</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div className="font-medium">{row.employee_name || row.employee_id}</div>
                  <div className="text-xs text-muted-foreground">{row.employee_id}</div>
                </TableCell>
                <TableCell className="text-sm">{row.contract_number}</TableCell>
                <TableCell>{titleize(row.contract_type)}</TableCell>
                <TableCell><Badge variant={statusVariant(row.effective_status || row.status)}>{titleize(row.effective_status || row.status)}</Badge></TableCell>
                <TableCell>
                  {row.end_date || '—'}
                  {typeof row.days_to_expiry === 'number' && row.status === 'active' && (
                    <span className={`ml-1 text-xs ${row.days_to_expiry < 0 ? 'text-red-600' : row.days_to_expiry <= 30 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      ({row.days_to_expiry < 0 ? `${-row.days_to_expiry}d ago` : `${row.days_to_expiry}d`})
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs">{titleize(row.esign_status)}</TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => refreshDetail(row.id)}><Eye className="h-3.5 w-3.5" /></Button>
                  {row.status === 'draft' && <Button size="sm" variant="outline" onClick={() => doActivate(row)} title="Activate"><CheckCircle2 className="h-3.5 w-3.5" /></Button>}
                  {['active'].includes(row.status) && <Button size="sm" variant="outline" onClick={() => openRenew(row)} title="Renew"><RotateCcw className="h-3.5 w-3.5" /></Button>}
                  {['active', 'draft'].includes(row.status) && <Button size="sm" variant="outline" onClick={() => doTerminate(row)} title="Terminate"><XCircle className="h-3.5 w-3.5" /></Button>}
                  {row.status !== 'renewed' && <Button size="sm" variant="outline" onClick={() => doCancel(row)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} contract{total === 1 ? '' : 's'} · Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Contract</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {(e.first_name || '') + ' ' + (e.last_name || '')} ({e.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.contract_type} onValueChange={(v) => setForm((f) => ({ ...f, contract_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Designation" />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>End Date <span className="text-muted-foreground text-xs">(blank = permanent)</span></Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
              </div>
              <div>
                <Label>Annual CTC</Label>
                <Input type="number" value={form.ctc_annual} onChange={(e) => setForm((f) => ({ ...f, ctc_annual: e.target.value }))} />
              </div>
              <div>
                <Label>Notice (days)</Label>
                <Input type="number" value={form.notice_period_days} onChange={(e) => setForm((f) => ({ ...f, notice_period_days: e.target.value }))} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.auto_renew} onChange={(e) => setForm((f) => ({ ...f, auto_renew: e.target.checked }))} />
              Auto-renew on expiry
            </label>
            <div>
              <Label>Terms</Label>
              <Textarea value={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail && !renewOpen} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {detail?.contract_number} — {titleize(detail?.contract_type)}
              {detail && <Badge className="ml-2" variant={statusVariant(detail.effective_status || detail.status)}>{titleize(detail.effective_status || detail.status)}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Employee:</span> {detail.employee_name || detail.employee_id}</div>
                <div><span className="text-muted-foreground">Title:</span> {detail.title || '—'}</div>
                <div><span className="text-muted-foreground">Start:</span> {detail.start_date || '—'}</div>
                <div><span className="text-muted-foreground">End:</span> {detail.end_date || 'Permanent'}</div>
                <div><span className="text-muted-foreground">Annual CTC:</span> {detail.ctc_annual ?? '—'}</div>
                <div><span className="text-muted-foreground">Notice:</span> {detail.notice_period_days ?? '—'} days</div>
              </div>
              <div className="border rounded p-3 space-y-2">
                <p className="text-xs uppercase text-muted-foreground">
                  E-Signature — {titleize(detail.esign_status)}
                  {detail.esign_provider ? ` · ${detail.esign_provider}` : ''}
                </p>
                <div className="flex items-center gap-4">
                  <span className={detail.employee_signed ? 'text-green-700' : 'text-muted-foreground'}>
                    {detail.employee_signed ? '✓' : '○'} Employee
                  </span>
                  <span className={detail.employer_signed ? 'text-green-700' : 'text-muted-foreground'}>
                    {detail.employer_signed ? '✓' : '○'} Employer
                  </span>
                </div>
                {(detail.signatures || []).map((s, i) => (
                  <div key={i} className="text-xs text-muted-foreground font-mono">
                    {titleize(s.party)}: {s.signer_email} · {String(s.signed_at).slice(0, 16)} · {String(s.signature_hash).slice(0, 12)}…
                  </div>
                ))}
                {['active', 'draft'].includes(detail.status) && (
                  <div className="flex gap-2">
                    {!detail.employee_signed && <Button size="sm" variant="outline" onClick={() => doSign(detail, 'employee')}>Record Employee Sign</Button>}
                    {!detail.employer_signed && <Button size="sm" variant="outline" onClick={() => doSign(detail, 'employer')}>Sign as Employer</Button>}
                  </div>
                )}
              </div>
              {detail.terms && <p><span className="text-muted-foreground">Terms:</span> {detail.terms}</p>}
              {detail.renewed_from_contract_id && (
                <p className="text-xs text-muted-foreground">Renewed from contract #{detail.renewed_from_contract_id}</p>
              )}
            </div>
          )}
          <DialogFooter>
            {detail?.status === 'active' && <Button variant="outline" onClick={() => openRenew(detail)}>Renew</Button>}
            {['active', 'draft'].includes(detail?.status) && <Button variant="outline" onClick={() => doTerminate(detail)}>Terminate</Button>}
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew dialog */}
      <Dialog open={renewOpen} onOpenChange={(o) => { setRenewOpen(o); if (!o) setDetail(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renew Contract {detail?.contract_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New End Date</Label>
              <Input type="date" value={renewForm.end_date} onChange={(e) => setRenewForm((f) => ({ ...f, end_date: e.target.value }))} />
            </div>
            <div>
              <Label>New Annual CTC</Label>
              <Input type="number" value={renewForm.ctc_annual} onChange={(e) => setRenewForm((f) => ({ ...f, ctc_annual: e.target.value }))} />
            </div>
            <p className="text-xs text-muted-foreground">Creates a new linked contract starting the day after the current end date; the current contract is marked renewed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRenewOpen(false); setDetail(null); }}>Cancel</Button>
            <Button onClick={submitRenew}>Renew</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
